/**
 * ConversationCustomerSelector — pílula da coluna CLIENTE do header.
 *
 * Pílula FECHADA não faz fetch nenhum: o nome vem enriquecido pelo backend em
 * `conversation.contact.customer`. A lista só é buscada quando o painel abre
 * (`enabled: open`), com busca server-side (`/customers/dropdown?search=`, que
 * casa por nome E por documento) e debounce de 300ms.
 *
 * Estado vazio segue o "Adicionar ao funil" do ConversationStageSelector:
 * pílula TRACEJADA lilás (D5). Sem `contacts:update` e sem cliente, não
 * renderiza nada — pílula de convite que não pode ser usada só frustra.
 *
 * Celular (`variant='mobile'`): botão de 44px (alvo de toque da Apple) com a
 * pílula visual de 36px dentro, e o painel vira bottom sheet.
 *
 * Rodapé do painel (quando já existe cliente vinculado): "Abrir cadastro
 * completo" (`customers:read`) abre `/crm/customers/:id/edit` em NOVA aba —
 * é o caminho pra endereço/segmento/site/domínios que o `QuickCreateCustomerDialog`
 * não cobre. Vive aqui, não dentro do modal de criação rápida (decisão
 * 2026-08-20): um link no modal ficaria inalcançável, porque `handleSubmit`
 * fecha o modal logo após criar — e o link "antes de criar" navegaria pra
 * fora da conversa. Aqui ele é descobrível a qualquer momento.
 */
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ConversationResponse } from '@troia-v3/types';

import { usePermissions } from '@/hooks/usePermissions';
import { Icon } from '@/shared/icons';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';

import { useSetContactCustomer } from '../hooks';
import { buildCustomerOptions, isCustomerListFull, sanitizeCustomerSearchTerm } from '../utils/customerOptions';
import QuickCreateCustomerDialog from './QuickCreateCustomerDialog';

const PURPLE = '#8b5cf6';

// Ícones inline: o catálogo `IconName` não tem "prédio", e depender de nome
// ausente vira o ícone de exclamação do pack incompleto.
const Building: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }} aria-hidden="true">
    <path
      d="M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15M14 10h4a2 2 0 0 1 2 2v9M3 21h18M8 8h2M8 12h2M8 16h2"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);
const ChevronDown: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }} aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Plus: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }} aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Itens do rodapé do painel — dentro do portal, que é tema claro fixo. */
export function customerFooterItemStyle(danger: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '9px 10px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    color: danger ? '#dc2626' : '#334155',
    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
  };
}

interface ConversationCustomerSelectorProps {
  conversation: ConversationResponse;
  variant?: 'desktop' | 'mobile';
}

const ConversationCustomerSelector: React.FC<ConversationCustomerSelectorProps> = ({
  conversation,
  variant = 'desktop',
}) => {
  const { hasModulePermission } = usePermissions();
  const canUpdate = hasModulePermission('contacts', 'update');
  const canCreateCustomer = hasModulePermission('customers', 'create');
  // Gate do item "Abrir cadastro completo" do rodapé — é a tela de Clientes,
  // então exige `customers:read` (correção 2026-08-20, rodada 2/5).
  const canOpenFullCustomer = hasModulePermission('customers', 'read');

  const contactId = conversation.contactId || conversation.contact?.id;
  const isIndividual = conversation.conversationType !== 'group';
  const customer = conversation.contact?.customer;

  if (!isIndividual || !contactId) return null;
  if (!customer && !canUpdate) return null;

  return (
    <CustomerSelectorInner
      contactId={contactId}
      customerId={customer?.id}
      customerName={customer?.name}
      canUpdate={canUpdate}
      canCreateCustomer={canCreateCustomer}
      canOpenFullCustomer={canOpenFullCustomer}
      variant={variant}
    />
  );
};

interface InnerProps {
  contactId: string;
  customerId?: string;
  customerName?: string;
  canUpdate: boolean;
  canCreateCustomer: boolean;
  canOpenFullCustomer: boolean;
  variant: 'desktop' | 'mobile';
}

const CustomerSelectorInner: React.FC<InnerProps> = ({
  contactId, customerId, customerName, canUpdate, canCreateCustomer, canOpenFullCustomer, variant,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  // O placeholder promete busca "por nome ou CNPJ", mas o backend faz regex
  // LITERAL contra `document` (só dígitos no banco) — buscar com a máscara que
  // o próprio sublabel exibe ("12.345.678/0001-99") daria zero resultados.
  // Sanitiza só o termo que VAI pro backend; o que o usuário vê no campo
  // continua exatamente o que ele digitou.
  const sanitizedSearch = useMemo(() => sanitizeCustomerSearchTerm(debouncedSearch), [debouncedSearch]);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { data: customers, isLoading } = useCustomersDropdown({ enabled: open, search: sanitizedSearch });
  const items = useMemo(() => customers ?? [], [customers]);
  const options = useMemo(() => buildCustomerOptions(items), [items]);

  const setCustomer = useSetContactCustomer();

  const isMobile = variant === 'mobile';
  const hasCustomer = !!customerName;

  const closePanel = () => { setOpen(false); setSearchTerm(''); };

  const applyCustomer = (nextCustomerId: string, successMessage: string) => {
    closePanel();
    setCustomer.mutate(
      { contactId, customerId: nextCustomerId },
      {
        onSuccess: () => toast.success(successMessage),
        onError: () => toast.error('Erro ao atualizar o cliente do contato'),
      },
    );
  };

  const buttonStyle: React.CSSProperties = isMobile
    ? { height: 44, padding: '4px 0', background: 'transparent', border: 'none', flex: '0 0 auto', cursor: canUpdate ? 'pointer' : 'default' }
    : { padding: 0, background: 'transparent', border: 'none', cursor: canUpdate ? 'pointer' : 'default' };

  // Réplica EXATA do objeto de estilo do "Adicionar ao funil" / etapa atual
  // (`ConversationStageSelector.tsx`, gatilhos `stage-add-to-funnel` e
  // `stage-selector-trigger`): sem altura fixa (padding define a altura),
  // padding PRÓPRIO por estado (dashed usa mais respiro, igual ao funil), e a
  // mesma classe `transition-colors` do hover. Só o mobile diverge — pílula de
  // 36px dentro do alvo de toque de 44px é decisão própria da Task 14, sem
  // equivalente no seletor de etapa (que não tem variant mobile).
  const pillClassName = 'inline-flex items-center gap-1.5 rounded-full transition-colors';

  const pillStyle = (dashed: boolean): React.CSSProperties => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: isMobile ? 210 : 168,
    ...(isMobile
      ? { height: 36, padding: '0 12px', fontSize: 13, fontWeight: 600 }
      : dashed
        ? { padding: '3px 10px', fontSize: 12, fontWeight: 500 }
        : { padding: '2px 8px', fontSize: 12, fontWeight: 500 }),
    ...(dashed
      ? { color: PURPLE, backgroundColor: 'rgba(139,92,246,0.10)', border: '1px dashed rgba(139,92,246,0.45)' }
      : { color: 'var(--theme-card-text)', backgroundColor: 'rgba(148,163,184,0.14)', border: '1px solid var(--theme-card-border)' }),
  });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!canUpdate) return;
          if (open) closePanel(); else setOpen(true);
        }}
        style={buttonStyle}
        title={hasCustomer ? customerName : 'Vincular cliente'}
        data-testid="chat-customer-trigger"
      >
        {hasCustomer ? (
          <span className={pillClassName} style={pillStyle(false)}>
            <Building />
            {/* ⚠️ As classes abaixo NÃO são decoração: são os ganchos que a cadeia de
                encolhimento da Task 12 usa. `.chat-hdr-pill-text` recebe `max-width:90px`
                no degrau ≤800 (reticências) e `display:none` no ≤720 (pílula vira só o
                ícone); `.chat-hdr-pill-chevron` some junto. Sem elas os degraus 3 e 4
                ficam inertes e os testes da Task 17 reprovam.
                E não há `style` inline nestes dois spans, de propósito: `display`, `overflow`
                e as reticências vivem em `.chat-hdr-pill-text` / `.chat-hdr-pill-chevron`
                no `src/index.css`. Estilo inline vence qualquer regra de CSS — com
                `display` inline, o `display: none` dos degraus 3 e 4 ficava inerte e só
                `!important` daria jeito (remendo: o degrau seguinte esbarraria nele). */}
            <span className="chat-hdr-pill-text">{customerName}</span>
            {canUpdate && (
              <span className="chat-hdr-pill-chevron">
                <ChevronDown />
              </span>
            )}
          </span>
        ) : (
          <span className={pillClassName} style={pillStyle(true)}>
            <Plus />
            {/* Mesmo gancho da pílula preenchida: sem ele o rótulo não ganhava reticências
                no degrau 3 nem virava só o "+" no degrau 4 — cortava seco no
                `overflow:hidden` da coluna. */}
            <span className="chat-hdr-pill-text">Vincular cliente</span>
          </span>
        )}
      </button>

      <SearchableSelect
        open={open}
        onOpenChange={(next) => { if (next) setOpen(true); else closePanel(); }}
        anchorRef={triggerRef}
        options={options}
        selectedId={customerId ?? null}
        onSelect={(id) => applyCustomer(id, 'Cliente vinculado ao contato')}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        isLoading={isLoading}
        searchPlaceholder="Buscar por nome ou CNPJ…"
        emptyLabel="Nenhum cliente encontrado"
        truncatedHint={isCustomerListFull(items) ? 'Mostrando os 100 primeiros — busque pelo nome ou documento' : undefined}
        variant={isMobile ? 'sheet' : 'popover'}
        testIdPrefix="chat-customer"
        footer={(canCreateCustomer || customerId) ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {canCreateCustomer && (
              <button
                type="button"
                /* NÃO limpa `searchTerm`: o termo digitado vira o nome do
                   cliente novo, poupando o atendente de redigitar.
                   `stopPropagation` é necessário: o wrapper do footer no
                   `SearchableSelect` tem `onClick={() => onOpenChange(false)}`,
                   que bubbling levaria a `closePanel()` (limpa `searchTerm`)
                   antes deste componente re-renderizar — anularia a intenção
                   acima em silêncio. */
                onClick={(e) => { e.stopPropagation(); setOpen(false); setQuickCreateOpen(true); }}
                style={customerFooterItemStyle(false)}
                data-testid="chat-customer-create"
              >
                <Icon name="plus" className="h-3.5 w-3.5" />
                Criar cliente
              </button>
            )}
            {customerId && canOpenFullCustomer && (
              // Nova aba de propósito: navegar na MESMA aba tiraria o atendente
              // da conversa — exatamente o que o atalho existe pra evitar.
              <a
                href={`/crm/customers/${customerId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...customerFooterItemStyle(false), textDecoration: 'none' }}
                data-testid="chat-customer-open-full"
              >
                <Icon name="external-link" className="h-3.5 w-3.5" />
                Abrir cadastro completo
              </a>
            )}
            {customerId && (
              <button
                type="button"
                onClick={() => applyCustomer('', 'Cliente desvinculado do contato')}
                style={customerFooterItemStyle(true)}
                data-testid="chat-customer-unlink"
              >
                <Icon name="x" className="h-3.5 w-3.5" />
                Desvincular
              </button>
            )}
          </div>
        ) : undefined}
      />

      <QuickCreateCustomerDialog
        isOpen={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onCreated={(created) => applyCustomer(created.id, `Cliente ${created.name} criado e vinculado`)}
        initialName={searchTerm}
      />
    </>
  );
};

export default ConversationCustomerSelector;
export { ConversationCustomerSelector };
